import { useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

// ============================================================================
// ICONS (inline SVG to avoid import dependencies)
// ============================================================================

function CoinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v12M9 9.5c0-.83 1.34-1.5 3-1.5s3 .67 3 1.5S14.66 11 12 11 9 11.67 9 12.5s1.34 1.5 3 1.5 3-.67 3-1.5" />
    </svg>
  );
}

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function GiftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" />
      <line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

// Social platform icons
function TwitterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.72a8.19 8.19 0 0 0 4.76 1.52V6.79a4.83 4.83 0 0 1-1-.1z" />
    </svg>
  );
}

function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

function ImageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
    </svg>
  );
}

function TrophyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  );
}

// ============================================================================
// MOCK DATA
// ============================================================================

interface MockReward {
  id: string;
  type: string;
  label: string;
  description: string;
  coins: number;
  icon: 'flame' | 'play' | 'twitter' | 'facebook' | 'instagram' | 'tiktok' | 'youtube' | 'share' | 'users' | 'gift' | 'star' | 'check' | 'image' | 'trophy';
  status: 'available' | 'claimed' | 'cooldown' | 'locked';
  cooldownEnds?: string; // ISO date
  cooldownLabel?: string; // e.g., "Weekly"
  progress?: { current: number; max: number }; // e.g., 3/5 ads today
  category: 'daily' | 'social' | 'ads' | 'referral' | 'milestone' | 'content';
  actionUrl?: string; // External URL to open
  actionLabel?: string; // Custom button text
}

const MOCK_STREAK = {
  currentStreak: 4,
  longestStreak: 12,
  todayClaimed: false,
  days: [
    { day: 1, coins: 15, claimed: true },
    { day: 2, coins: 15, claimed: true },
    { day: 3, coins: 25, claimed: true, milestone: true },
    { day: 4, coins: 15, claimed: true },
    { day: 5, coins: 15, claimed: false },
    { day: 6, coins: 15, claimed: false },
    { day: 7, coins: 75, claimed: false, milestone: true },
  ],
};

// Weekly cooldown helper — next Monday at midnight
const nextWeeklyCooldown = new Date();
nextWeeklyCooldown.setDate(nextWeeklyCooldown.getDate() + (8 - nextWeeklyCooldown.getDay()) % 7);
nextWeeklyCooldown.setHours(0, 0, 0, 0);
const WEEKLY_COOLDOWN = nextWeeklyCooldown.toISOString();

// Cooldown 3 days from now (for demo of mid-week state)
const midWeekCooldown = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();

const MOCK_REWARDS: MockReward[] = [
  // Daily
  { id: 'daily-login', type: 'DAILY_LOGIN', label: 'Daily Login', description: 'Claim your daily reward', coins: 15, icon: 'flame', status: 'available', category: 'daily' },

  // Ads — 10c each, 3 min cooldown, 5/day
  { id: 'ad-1', type: 'AD_WATCH', label: 'Watch Ad', description: 'Watch a short video (3 min cooldown)', coins: 10, icon: 'play', status: 'available', category: 'ads', progress: { current: 2, max: 5 } },

  // ---- CONTENT CREATION ----
  { id: 'share-prediction', type: 'SHARE_PREDICTION_CARD', label: 'Share Prediction Card', description: 'Generate and share your prop picks as a shareable card', coins: 40, icon: 'image', status: 'available', category: 'content', actionLabel: 'Create Card', cooldownLabel: 'Weekly' },
  { id: 'share-winner', type: 'SHARE_WINNER_CARD', label: 'Share Winner Card', description: 'Share your winning picks after an event settles', coins: 50, icon: 'trophy', status: 'available', category: 'content', actionLabel: 'Create Card', cooldownLabel: 'Weekly' },
  { id: 'share-event-invite', type: 'SHARE_EVENT_INVITE', label: 'Share Event Invite', description: 'Share an event invite link with your referral code', coins: 25, icon: 'share', status: 'available', category: 'content', actionLabel: 'Share Link', cooldownLabel: 'Weekly' },

  // ---- TWITTER / X ----
  { id: 'twitter-follow', type: 'SOCIAL_FOLLOW_TWITTER', label: 'Follow on X', description: 'Follow @FantasyPRIME on X', coins: 50, icon: 'twitter', status: 'available', category: 'social', actionUrl: 'https://x.com/FantasyPRIME', actionLabel: 'Follow' },
  { id: 'twitter-repost', type: 'SOCIAL_REPOST_TWITTER', label: 'Repost on X', description: 'Repost our pinned post', coins: 25, icon: 'twitter', status: 'cooldown', cooldownEnds: midWeekCooldown, cooldownLabel: 'Weekly', category: 'social' },
  { id: 'twitter-post', type: 'SOCIAL_POST_TWITTER', label: 'Post about PRIME', description: 'Tweet about FantasyPRIME and tag us', coins: 35, icon: 'twitter', status: 'available', category: 'social', actionLabel: 'Post', cooldownLabel: 'Weekly' },

  // ---- FACEBOOK ----
  { id: 'facebook-follow', type: 'SOCIAL_FOLLOW_FACEBOOK', label: 'Follow on Facebook', description: 'Like our Facebook page', coins: 50, icon: 'facebook', status: 'available', category: 'social', actionUrl: 'https://facebook.com/FantasyPRIME', actionLabel: 'Like Page' },
  { id: 'facebook-share', type: 'SOCIAL_SHARE_FACEBOOK', label: 'Share on Facebook', description: 'Share our latest post to your timeline', coins: 25, icon: 'facebook', status: 'available', category: 'social', actionLabel: 'Share', cooldownLabel: 'Weekly' },

  // ---- INSTAGRAM ----
  { id: 'instagram-follow', type: 'SOCIAL_FOLLOW_INSTAGRAM', label: 'Follow on Instagram', description: 'Follow @fantasyprime on Instagram', coins: 50, icon: 'instagram', status: 'claimed', category: 'social' },
  { id: 'instagram-story', type: 'SOCIAL_STORY_INSTAGRAM', label: 'Share to IG Story', description: 'Share a prediction card or winner card to your story and tag us', coins: 35, icon: 'instagram', status: 'available', category: 'social', actionLabel: 'Share Story', cooldownLabel: 'Weekly' },

  // ---- TIKTOK ----
  { id: 'tiktok-follow', type: 'SOCIAL_FOLLOW_TIKTOK', label: 'Follow on TikTok', description: 'Follow @fantasyprime on TikTok', coins: 50, icon: 'tiktok', status: 'available', category: 'social', actionUrl: 'https://tiktok.com/@fantasyprime', actionLabel: 'Follow' },
  { id: 'tiktok-comment', type: 'SOCIAL_COMMENT_TIKTOK', label: 'Comment on TikTok', description: 'Comment on our latest TikTok video', coins: 25, icon: 'tiktok', status: 'cooldown', cooldownEnds: WEEKLY_COOLDOWN, cooldownLabel: 'Weekly', category: 'social' },

  // ---- YOUTUBE ----
  { id: 'youtube-subscribe', type: 'SOCIAL_SUBSCRIBE_YOUTUBE', label: 'Subscribe on YouTube', description: 'Subscribe to FantasyPRIME on YouTube', coins: 50, icon: 'youtube', status: 'available', category: 'social', actionUrl: 'https://youtube.com/@FantasyPRIME', actionLabel: 'Subscribe' },
  { id: 'youtube-like', type: 'SOCIAL_LIKE_YOUTUBE', label: 'Like a Video', description: 'Like our latest YouTube video', coins: 25, icon: 'youtube', status: 'available', category: 'social', actionLabel: 'Watch & Like', cooldownLabel: 'Weekly' },

  // ---- DISCORD ----
  { id: 'discord-join', type: 'SOCIAL_JOIN_DISCORD', label: 'Join Discord', description: 'Join the FantasyPRIME Discord server', coins: 50, icon: 'share', status: 'available', category: 'social', actionUrl: 'https://discord.gg/fantasyprime', actionLabel: 'Join' },

  // Referrals — only trigger on qualifying actions, not signups
  { id: 'referral-event', type: 'REFERRAL_FIRST_EVENT', label: 'Friend Enters Event', description: '200c when a referred friend enters their first event', coins: 200, icon: 'users', status: 'available', category: 'referral' },
  { id: 'referral-coins', type: 'REFERRAL_BUYS_COINS', label: 'Friend Buys Coins', description: '300c when a referred friend purchases coins', coins: 300, icon: 'users', status: 'available', category: 'referral' },

  // Milestones
  { id: 'profile-complete', type: 'PROFILE_COMPLETE', label: 'Complete Profile', description: 'Add avatar and social links', coins: 50, icon: 'check', status: 'available', category: 'milestone' },
  { id: 'first-event', type: 'FIRST_EVENT_ENTRY', label: 'First Event', description: 'Enter your first event', coins: 75, icon: 'star', status: 'claimed', category: 'milestone' },
  { id: 'first-prop', type: 'FIRST_PROP_PURCHASE', label: 'First Prop Boost', description: 'Buy your first prop boost', coins: 25, icon: 'gift', status: 'locked', category: 'milestone' },
];

const MOCK_REFERRAL_STATS = {
  totalReferred: 3,
  totalCoinsEarned: 700,
  referralCode: 'PRIME-7X9K2',
  pending: [
    { name: 'Alex M.', action: 'Entered first event', coins: 200, date: '2026-03-28' },
    { name: 'Jordan R.', action: 'Bought coins', coins: 300, date: '2026-03-30' },
  ],
  qualifiedActions: [
    { name: 'Sam K.', action: 'Entered first event', coins: 200, date: '2026-03-29' },
  ],
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function IconForType({ icon, className }: { icon: MockReward['icon']; className?: string }) {
  switch (icon) {
    case 'flame': return <FlameIcon className={className} />;
    case 'play': return <PlayIcon className={className} />;
    case 'twitter': return <TwitterIcon className={className} />;
    case 'facebook': return <FacebookIcon className={className} />;
    case 'instagram': return <InstagramIcon className={className} />;
    case 'tiktok': return <TikTokIcon className={className} />;
    case 'youtube': return <YouTubeIcon className={className} />;
    case 'share': return <ShareIcon className={className} />;
    case 'users': return <UsersIcon className={className} />;
    case 'gift': return <GiftIcon className={className} />;
    case 'star': return <StarIcon className={className} />;
    case 'check': return <CheckCircleIcon className={className} />;
    case 'image': return <ImageIcon className={className} />;
    case 'trophy': return <TrophyIcon className={className} />;
  }
}

function CooldownTimer({ endsAt, label }: { endsAt: string; label?: string }) {
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    const update = () => {
      const diff = new Date(endsAt).getTime() - Date.now();
      if (diff <= 0) { setRemaining('Ready!'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      if (d > 0) setRemaining(`${d}d ${h}h`);
      else setRemaining(h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    update();
    const iv = setInterval(update, 60000);
    return () => clearInterval(iv);
  }, [endsAt]);

  return (
    <span className="flex items-center gap-1 text-[10px] text-warm-gray/50">
      <ClockIcon className="w-3 h-3" /> {label && <span className="text-warm-gray/30">{label}</span>} {remaining}
    </span>
  );
}

function CoinBadge({ amount, size = 'md' }: { amount: number; size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: 'text-[10px] px-1.5 py-0.5 gap-0.5',
    md: 'text-xs px-2 py-1 gap-1',
    lg: 'text-sm px-3 py-1.5 gap-1.5',
  };
  return (
    <span className={cn('inline-flex items-center font-bold rounded-full bg-gold/15 text-gold border border-gold/20', sizes[size])}>
      <CoinIcon className={size === 'sm' ? 'w-2.5 h-2.5' : size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} />
      +{amount}
    </span>
  );
}

// ============================================================================
// STREAK SECTION (compact)
// ============================================================================

function StreakSection({ streak, onClaim }: { streak: typeof MOCK_STREAK; onClaim: () => void }) {
  return (
    <div className="rounded-lg border border-surface-3 bg-surface-2 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <FlameIcon className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-xs font-bold text-cream">Daily Streak</span>
          <span className="text-[10px] text-warm-gray/50">{streak.currentStreak}d</span>
        </div>
        <span className="text-[9px] text-warm-gray/40">Best: {streak.longestStreak}d</span>
      </div>
      {/* Day markers — compact */}
      <div className="flex items-center gap-1 mb-2">
        {streak.days.map((d) => (
          <div key={d.day} className="flex-1 flex flex-col items-center">
            <div className={cn(
              'w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold',
              d.claimed
                ? 'bg-gold/20 border border-gold text-gold'
                : d.milestone
                  ? 'bg-surface-3 border border-dashed border-gold/30 text-warm-gray/50'
                  : 'bg-surface-3 text-warm-gray/40',
            )}>
              {d.claimed ? <CheckCircleIcon className="w-3 h-3" /> : d.milestone ? <StarIcon className="w-3 h-3 text-gold/40" /> : d.day}
            </div>
            <span className={cn('text-[8px] mt-0.5', d.claimed ? 'text-gold' : 'text-warm-gray/30')}>+{d.coins}</span>
          </div>
        ))}
      </div>
      {/* Claim */}
      {!streak.todayClaimed ? (
        <button onClick={onClaim} className="w-full py-1.5 rounded bg-gold/15 border border-gold/30 text-gold font-bold text-[11px] hover:bg-gold/25 transition-all flex items-center justify-center gap-1.5">
          Claim Day {streak.currentStreak + 1} <CoinBadge amount={streak.days[streak.currentStreak]?.coins ?? 15} size="sm" />
        </button>
      ) : (
        <div className="text-center py-1 text-[10px] text-warm-gray/40 flex items-center justify-center gap-1">
          <CheckCircleIcon className="w-3 h-3 text-verdigris" /> Claimed — back tomorrow!
        </div>
      )}
    </div>
  );
}

// ============================================================================
// REWARD ROW (compact single-line)
// ============================================================================

function RewardRow({ reward, onClaim }: { reward: MockReward; onClaim: (id: string) => void }) {
  const isClaimed = reward.status === 'claimed';
  const isLocked = reward.status === 'locked';
  const isCooldown = reward.status === 'cooldown';
  const isAvailable = reward.status === 'available';

  return (
    <div className={cn(
      'flex items-center gap-2 py-1.5 px-2 rounded-lg transition-all',
      isAvailable ? 'hover:bg-surface-3/30' : 'opacity-60',
    )}>
      {/* Icon */}
      <div className={cn('w-6 h-6 rounded flex items-center justify-center shrink-0',
        isClaimed ? 'bg-verdigris/10' : isLocked ? 'bg-surface-3' : isCooldown ? 'bg-surface-3' : 'bg-gold/10',
      )}>
        {isLocked ? <LockIcon className="w-3 h-3 text-warm-gray/30" /> : <IconForType icon={reward.icon} className={cn('w-3 h-3', isClaimed ? 'text-verdigris' : isCooldown ? 'text-warm-gray/40' : 'text-gold')} />}
      </div>

      {/* Label + description */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={cn('text-[11px] font-semibold truncate', isClaimed || isLocked ? 'text-warm-gray/50' : 'text-cream')}>{reward.label}</span>
          {reward.cooldownLabel && !isClaimed && <span className="text-[8px] text-warm-gray/30 bg-surface-3 rounded px-1 py-0.5 shrink-0">{reward.cooldownLabel}</span>}
        </div>
      </div>

      {/* Progress bar (ads) */}
      {reward.progress && isAvailable && (
        <div className="flex items-center gap-1 shrink-0">
          <div className="w-12 h-1 bg-surface-3 rounded-full overflow-hidden">
            <div className="h-full bg-gold/60 rounded-full" style={{ width: `${(reward.progress.current / reward.progress.max) * 100}%` }} />
          </div>
          <span className="text-[9px] text-warm-gray/40">{reward.progress.current}/{reward.progress.max}</span>
        </div>
      )}

      {/* Coins */}
      {!isClaimed && <span className="text-[10px] font-bold text-gold shrink-0">+{reward.coins}</span>}

      {/* Action */}
      {isAvailable && (
        <div className="flex items-center gap-1 shrink-0">
          {reward.actionUrl && (
            <a href={reward.actionUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-0.5 rounded text-[10px] font-semibold bg-surface-3 text-cream hover:bg-surface-4 transition-all">{reward.actionLabel || 'Go'}</a>
          )}
          <button onClick={() => onClaim(reward.id)} className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gold/15 text-gold hover:bg-gold/25 border border-gold/30 transition-all">
            {reward.actionUrl ? 'Claim' : (reward.actionLabel || 'Claim')}
          </button>
        </div>
      )}
      {isCooldown && reward.cooldownEnds && <CooldownTimer endsAt={reward.cooldownEnds} />}
      {isClaimed && <span className="text-[9px] text-verdigris font-medium shrink-0 flex items-center gap-0.5"><CheckCircleIcon className="w-2.5 h-2.5" />Done</span>}
      {isLocked && <span className="text-[9px] text-warm-gray/30 italic shrink-0">Locked</span>}
    </div>
  );
}

// ============================================================================
// REFERRAL SECTION (compact)
// ============================================================================

function ReferralSection({ stats }: { stats: typeof MOCK_REFERRAL_STATS }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(`https://fantasyprime.com/join?ref=${stats.referralCode}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [stats.referralCode]);

  return (
    <div className="rounded-lg border border-surface-3 bg-surface-2 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <UsersIcon className="w-3.5 h-3.5 text-gold" />
          <span className="text-xs font-bold text-cream">Referrals</span>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="text-warm-gray/50">{stats.totalReferred} friends</span>
          <span className="text-gold font-bold">{stats.totalCoinsEarned}c earned</span>
        </div>
      </div>
      <p className="text-[10px] text-warm-gray/40">200c when a friend enters an event + 300c when they buy coins</p>
      <div className="flex items-center gap-1.5">
        <div className="flex-1 bg-surface-3 rounded px-2 py-1 text-[10px] text-cream/60 font-mono truncate border border-surface-4">
          fantasyprime.com/join?ref={stats.referralCode}
        </div>
        <button onClick={handleCopy} className={cn('px-2 py-1 rounded text-[10px] font-semibold transition-all whitespace-nowrap', copied ? 'bg-verdigris/15 text-verdigris' : 'bg-gold/15 text-gold hover:bg-gold/25 border border-gold/30')}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <button className="px-2 py-1 rounded text-[10px] font-semibold bg-surface-3 text-cream/70 hover:text-cream transition-all"><TwitterIcon className="w-3 h-3" /></button>
      </div>
      {/* Recent */}
      {(stats.pending.length > 0 || stats.qualifiedActions.length > 0) && (
        <div className="space-y-0.5 pt-1 border-t border-surface-3/50">
          {[...stats.qualifiedActions, ...stats.pending].map((item, i) => (
            <div key={i} className="flex items-center justify-between text-[10px] py-0.5">
              <span className="text-cream/60">{item.name} <span className="text-warm-gray/40">{item.action}</span></span>
              <span className="text-gold font-bold">+{item.coins}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COIN EARNED TOAST
// ============================================================================

function CoinToast({ amount, visible }: { amount: number; visible: boolean }) {
  if (!visible) return null;
  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-bounce">
      <div className="bg-surface-2 border-2 border-gold/40 rounded-lg px-4 py-2 shadow-2xl flex items-center gap-2">
        <CoinIcon className="w-4 h-4 text-gold" />
        <span className="text-sm font-bold text-gold">+{amount} Coins!</span>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function EarnCoinsPreview() {
  const [balance, setBalance] = useState(1250);
  const [streak, setStreak] = useState(MOCK_STREAK);
  const [rewards, setRewards] = useState(MOCK_REWARDS);
  const [toast, setToast] = useState<{ amount: number; visible: boolean }>({ amount: 0, visible: false });

  const showToast = useCallback((amount: number) => {
    setToast({ amount, visible: true });
    setTimeout(() => setToast({ amount: 0, visible: false }), 2500);
  }, []);

  const handleStreakClaim = useCallback(() => {
    const dayCoins = streak.days[streak.currentStreak]?.coins ?? 25;
    setBalance((b) => b + dayCoins);
    setStreak((s) => ({
      ...s,
      todayClaimed: true,
      currentStreak: s.currentStreak + 1,
      days: s.days.map((d, i) => i === s.currentStreak ? { ...d, claimed: true } : d),
    }));
    showToast(dayCoins);
  }, [streak, showToast]);

  const handleClaim = useCallback((id: string) => {
    const reward = rewards.find((r) => r.id === id);
    if (!reward || reward.status !== 'available') return;

    setBalance((b) => b + reward.coins);
    setRewards((rs) => rs.map((r) =>
      r.id === id
        ? { ...r, status: 'claimed' as const }
        : r
    ));
    showToast(reward.coins);
  }, [rewards, showToast]);

  const adRewards = rewards.filter((r) => r.category === 'ads');
  const contentRewards = rewards.filter((r) => r.category === 'content');
  const socialRewards = rewards.filter((r) => r.category === 'social');
  const milestoneRewards = rewards.filter((r) => r.category === 'milestone');

  // Group social by platform for cleaner display
  const socialPlatforms = [
    { key: 'twitter', label: 'X / Twitter', icon: <TwitterIcon className="w-3.5 h-3.5" /> },
    { key: 'facebook', label: 'Facebook', icon: <FacebookIcon className="w-3.5 h-3.5" /> },
    { key: 'instagram', label: 'Instagram', icon: <InstagramIcon className="w-3.5 h-3.5" /> },
    { key: 'tiktok', label: 'TikTok', icon: <TikTokIcon className="w-3.5 h-3.5" /> },
    { key: 'youtube', label: 'YouTube', icon: <YouTubeIcon className="w-3.5 h-3.5" /> },
  ];

  // Count available social rewards
  const socialAvailable = socialRewards.filter((r) => r.status === 'available').length;
  const socialTotal = socialRewards.length;

  return (
    <div className="min-h-screen bg-surface text-cream">
      <CoinToast amount={toast.amount} visible={toast.visible} />

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-cream">Earn Coins</h1>
            <p className="text-xs text-warm-gray/50 mt-0.5">Complete actions to earn coins for prop boosts</p>
          </div>
          <div className="flex items-center gap-2 bg-surface-2 border border-surface-3 rounded-lg px-3 py-2">
            <CoinIcon className="w-4 h-4 text-gold" />
            <span className="text-sm font-bold text-cream">{balance.toLocaleString()}</span>
          </div>
        </div>

        {/* Daily Streak */}
        <StreakSection streak={streak} onClaim={handleStreakClaim} />

        {/* Watch Ads */}
        {adRewards.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <PlayIcon className="w-3.5 h-3.5 text-warm-gray/40" />
              <h2 className="text-[10px] text-warm-gray/50 font-medium uppercase tracking-wider">Watch Ads</h2>
            </div>
            {adRewards.map((r) => <RewardRow key={r.id} reward={r} onClaim={handleClaim} />)}
          </div>
        )}

        {/* Content Creation */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <ImageIcon className="w-3.5 h-3.5 text-warm-gray/40" />
            <h2 className="text-[10px] text-warm-gray/50 font-medium uppercase tracking-wider">Share Your Picks</h2>
          </div>
          <p className="text-[11px] text-warm-gray/40 -mt-1">Create shareable cards from your predictions and wins</p>
          {contentRewards.map((r) => <RewardRow key={r.id} reward={r} onClaim={handleClaim} />)}
        </div>

        {/* Social Actions — grouped by platform */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShareIcon className="w-3.5 h-3.5 text-warm-gray/40" />
              <h2 className="text-[10px] text-warm-gray/50 font-medium uppercase tracking-wider">Social Actions</h2>
            </div>
            <span className="text-[10px] text-warm-gray/30">{socialAvailable}/{socialTotal} available</span>
          </div>

          {socialPlatforms.map(({ key, label, icon }) => {
            const platformRewards = socialRewards.filter((r) => r.icon === key);
            if (platformRewards.length === 0) return null;
            const allClaimed = platformRewards.every((r) => r.status === 'claimed');
            const allCooldown = platformRewards.every((r) => r.status === 'cooldown' || r.status === 'claimed');

            return (
              <div key={key} className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <span className={cn('opacity-60', allClaimed ? 'text-verdigris' : allCooldown ? 'text-warm-gray/30' : 'text-cream/70')}>{icon}</span>
                  <span className={cn('text-[11px] font-medium', allClaimed ? 'text-verdigris/70' : allCooldown ? 'text-warm-gray/30' : 'text-cream/60')}>{label}</span>
                  {allClaimed && <CheckCircleIcon className="w-3 h-3 text-verdigris/50" />}
                </div>
                {platformRewards.map((r) => <RewardRow key={r.id} reward={r} onClaim={handleClaim} />)}
              </div>
            );
          })}
        </div>

        {/* Referrals */}
        <ReferralSection stats={MOCK_REFERRAL_STATS} />

        {/* Milestones */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <StarIcon className="w-3.5 h-3.5 text-warm-gray/40" />
            <h2 className="text-[10px] text-warm-gray/50 font-medium uppercase tracking-wider">Milestones</h2>
          </div>
          {milestoneRewards.map((r) => <RewardRow key={r.id} reward={r} onClaim={handleClaim} />)}
        </div>

        {/* Earning summary */}
        <div className="rounded-xl border border-surface-3/50 bg-surface-2/50 p-4">
          <p className="text-[10px] text-warm-gray/40 font-medium uppercase tracking-wider mb-2">Weekly Earning Potential</p>
          <div className="grid grid-cols-5 gap-2 text-center">
            <div>
              <div className="text-sm font-bold text-gold">115c</div>
              <div className="text-[9px] text-warm-gray/40">Streaks</div>
            </div>
            <div>
              <div className="text-sm font-bold text-gold">350c</div>
              <div className="text-[9px] text-warm-gray/40">Ads</div>
            </div>
            <div>
              <div className="text-sm font-bold text-gold">115c</div>
              <div className="text-[9px] text-warm-gray/40">Content</div>
            </div>
            <div>
              <div className="text-sm font-bold text-gold">195c</div>
              <div className="text-[9px] text-warm-gray/40">Social</div>
            </div>
            <div>
              <div className="text-sm font-bold text-cream">~775c</div>
              <div className="text-[9px] text-warm-gray/50 font-bold">Total</div>
            </div>
          </div>
          <p className="text-[9px] text-warm-gray/30 text-center mt-2">+ referral bonuses (uncapped) &middot; ~3,000c/month max</p>
        </div>
      </div>
    </div>
  );
}
